/* WHERE THE HOSTED CHART OPENS.
 *
 * ═══ WHY THIS EXISTS AT ALL ═══
 *
 * The opening view was `[-123.9, 49.3]`, zoom 9 — the Strait of Georgia, which
 * is where the boat is and was therefore a reasonable thing to hard-code while
 * this deployment had one audience. It stops being reasonable the moment the
 * chart draws somewhere else: a link to a Guna Yala anchorage that lands in
 * British Columbia is a link to nothing, and a chart that forgets where you
 * were every time you reload is a chart you re-navigate to before you can use
 * it.
 *
 * Three sources, in falling order of how deliberate they are:
 *
 *   1. the URL          — someone MEANT this view; it wins over everything
 *   2. the last view    — where this browser was, so a reload is not a reset
 *   3. the fallback     — the Strait of Georgia, unchanged
 *
 * ═══ WHAT THIS IS NOT ═══
 *
 * It is not a region concept. Nothing here selects packs, coverage or chart
 * sources — a layer that does not cover the water under the view already greys
 * itself out with a reason, which is the mechanism that makes "just point it
 * somewhere else" work at all. Adding a region enum on top of that would be a
 * second, worse copy of `Coverage`.
 *
 * The `<Chart>` component's own `center`/`zoom` defaults and the custom
 * element's `center=` attribute are deliberately NOT touched. Those are the
 * embed API — a page embedding the element states its own view — and this is
 * one host's opening screen.
 */

/** Where the chart opens when nothing else has an opinion: the Strait of
 *  Georgia, which is the water this was built for. */
export const FALLBACK: View = { center: [-123.9, 49.3], zoom: 9 };

export interface View {
  /** [lon, lat] — MapLibre's order, not a chart plotter's. */
  center: [number, number];
  zoom: number;
}

const KEY = 'gd.view';

/** MapLibre's own limits. A view outside them is not a view. */
const inRange = (lon: number, lat: number, zoom: number): boolean =>
  Number.isFinite(lon) && lon >= -180 && lon <= 180 &&
  Number.isFinite(lat) && lat >= -85 && lat <= 85 &&
  Number.isFinite(zoom) && zoom >= 0 && zoom <= 22;

/**
 * `?center=lon,lat&zoom=n`, or null.
 *
 * LON,LAT and not lat,lon. That is MapLibre's order and the order every
 * `center` in this codebase already uses, and a shared link whose two numbers
 * are the wrong way round is a link that silently lands in the wrong ocean
 * rather than failing. The 85-degree latitude clamp is what catches most of
 * them: a swapped Guna Yala pair is (9.58, -78.88), which parses as a longitude
 * of 9.58 and a LATITUDE of -78.88 — inside the clamp, in the Southern Ocean.
 * So it cannot be caught here, and the argument for matching the rest of the
 * codebase rather than inventing a second convention is the strongest one
 * available.
 *
 * A malformed value is ignored rather than throwing: the next source down is a
 * perfectly good answer, and a chart that refuses to open because a query
 * string was mistyped is worse than one that opens at home.
 */
export function viewFromParams(search: string): View | null {
  const q = new URLSearchParams(search);
  const c = q.get('center');
  if (!c) return null;
  const parts = c.split(',');
  if (parts.length !== 2) return null;
  const lon = Number(parts[0]);
  const lat = Number(parts[1]);
  /* Zoom is optional and independent: `?center=` alone means "here, at the
   * zoom I would have got anyway", which is the useful reading. */
  const zoom = q.has('zoom') ? Number(q.get('zoom')) : FALLBACK.zoom;
  if (!inRange(lon, lat, zoom)) return null;
  return { center: [lon, lat], zoom };
}

/** The last view this browser had, or null. Storage being unavailable — a
 *  locked-down browser, private mode on some engines — is not a failure worth
 *  reporting; it just means there is no remembered view. */
export function rememberedView(): View | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const v = JSON.parse(raw) as Partial<View>;
    const lon = Number(v.center?.[0]);
    const lat = Number(v.center?.[1]);
    const zoom = Number(v.zoom);
    if (!inRange(lon, lat, zoom)) return null;
    return { center: [lon, lat], zoom };
  } catch {
    /* Someone else's key, or a half-written value. Same answer as none. */
    return null;
  }
}

/** The view to open at. */
export function openingView(search: string = location.search): View {
  return viewFromParams(search) ?? rememberedView() ?? FALLBACK;
}

/** Store a view, if the browser will have it. */
export function storeView(v: View): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(v));
  } catch {
    /* Quota, or storage denied. The chart works; it just will not remember. */
  }
}

/**
 * Remember where the map is, from now on.
 *
 * On `moveend` rather than on every frame — the value is only read at startup,
 * so writing it sixty times a second during a pan buys nothing and costs a
 * synchronous storage write per frame. Returns an unsubscribe so a caller with
 * a lifecycle can use one.
 */
export function rememberView(map: {
  on: (ev: 'moveend', fn: () => void) => void;
  off: (ev: 'moveend', fn: () => void) => void;
  getCenter: () => { lng: number; lat: number };
  getZoom: () => number;
}): () => void {
  const onMoveEnd = () => {
    const c = map.getCenter();
    storeView({ center: [c.lng, c.lat], zoom: map.getZoom() });
  };
  map.on('moveend', onMoveEnd);
  return () => map.off('moveend', onMoveEnd);
}
